/**
 * LoginScreen — the front door at bigxbook.app.
 *
 * Two ways in, sharing one set of fields and one error line:
 *
 *   phone → PIN   the everyday path. Works with no SMS provider configured,
 *                 and on a counter with no mobile signal, which is the more
 *                 lasting reason.
 *   phone → OTP   kept for when a DLT-registered SMS template is live.
 *
 * ── Why registering is a separate button ───────────────────────────────────
 * The OTP path could afford one set of fields for both, because `verify-otp`
 * creates a PENDING merchant for any number it has never seen. PIN login
 * cannot: distinguishing an unknown number from a wrong PIN is precisely what
 * the server refuses to do, so that this endpoint cannot be used to discover
 * which numbers are registered. So the person says which one they are — which
 * also reads plainer at a counter than a form that silently changes meaning.
 *
 * ── Why registering is a BUTTON, next to signing in ────────────────────────
 * It used to be a quiet link below a fold of explanatory text, under a primary
 * labelled "Continue". A new merchant typed their number, pressed the only
 * thing that looked pressable, and was told "Incorrect mobile number or PIN"
 * for an account that had never existed — a dead end reached by doing the
 * obvious thing. The two paths are now two buttons, side by side in the same
 * form, named for what they do; and every place the wrong one can be reached
 * carries a one-tap way across to the other.
 *
 * Visually the ground stays light and the form alone is charcoal glass — the
 * inverse of the app shell, where charcoal chrome frames a light content sheet.
 * `dark chrome` is therefore scoped to the card rather than the page; it
 * re-points the semantic tokens for that subtree exactly as it does in
 * AppShell, so everything inside the card reads light-on-charcoal (see
 * theme.css).
 */
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useState, type FormEvent } from 'react';
import { useAuth } from '@/auth/useAuth';
import { BrandMark } from '@/components/BrandMark';
// Login is the first of five screens a new merchant sees in one sitting, so it
// shares its field and button styling with the onboarding screens rather than
// keeping a second copy that can drift. See OnboardingCard.
import { FIELD, PRIMARY, SECONDARY, QUIET } from './onboarding/OnboardingCard';

/** Mirrors the server rule. The server remains the authority; this is courtesy. */
const PIN_SHAPE = /^(\d{4}|\d{6})$/;

export function LoginScreen() {
  const auth = useAuth();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const trimmedPhone = phone.trim();
  const phoneLooksRight = /^\d{10}$/.test(trimmedPhone);

  // Every handler clears `localError` first: it belongs to the field that was
  // on screen when it was raised, and a stale one would otherwise sit on top of
  // the server's answer to the next thing typed (shownError prefers local).
  const onSubmitPhone = (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (phoneLooksRight) auth.goToPin(trimmedPhone);
  };

  const onSubmitOtp = (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (otp.trim()) void auth.submitOtp(otp.trim());
  };

  const onSubmitPin = (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (pin.trim()) void auth.submitPin(pin.trim());
  };

  /** From a failed sign-in to the registration form, keeping the number. */
  const switchToRegister = () => {
    setPin('');
    setConfirmPin('');
    setLocalError(null);
    auth.goToRegister(auth.phone || trimmedPhone);
  };

  /** The reverse: a number that turned out to have an account already. */
  const switchToPin = () => {
    setPin('');
    setConfirmPin('');
    setLocalError(null);
    auth.goToPin(auth.phone || trimmedPhone);
  };

  /**
   * The registration path, from the phone stage.
   *
   * The number is needed before the PIN form is worth showing, but refusing to
   * react at all until one is typed is how the old quiet link went unnoticed.
   * So the button always answers: either it moves on, or it says what is
   * missing.
   */
  const onClickRegister = () => {
    if (!phoneLooksRight) {
      setLocalError('Enter your 10-digit mobile number first, then create your account.');
      return;
    }
    setLocalError(null);
    auth.goToRegister(trimmedPhone);
  };

  const onSubmitRegister = (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!PIN_SHAPE.test(pin)) {
      setLocalError('PIN must be exactly 4 or 6 digits.');
      return;
    }
    if (pin !== confirmPin) {
      // Caught here rather than at the server: a mismatch is not the server's
      // business, and a round trip to be told you typed it twice differently
      // is a poor use of a counter's time.
      setLocalError('The two PINs do not match.');
      return;
    }
    void auth.submitRegistration(auth.phone || trimmedPhone, pin);
  };

  const backToStart = () => {
    setPin('');
    setConfirmPin('');
    setOtp('');
    setLocalError(null);
    auth.back();
  };

  const shownError = localError ?? auth.error;
  useErrorPopup(shownError, () => setLocalError(null));

  return (
    // flex-col rather than `grid place-items-center`, so the card and the
    // parent-brand line below it centre as one group.
    <main className="login-backdrop flex min-h-screen flex-col items-center justify-center p-6">
      {/* `dark chrome` is scoped to the card, not the page: the ground stays
          light and the charcoal is the object sitting on it. */}
      <div className="dark chrome glass-charcoal w-full max-w-sm rounded-2xl border border-white/10 p-8">
        <header className="mb-7">
          {/* The brand ramp, not the app ramp: this is the one screen that is
              a brand moment rather than a work surface. */}
          <div className="mb-3 flex items-center gap-3">
            <BrandMark size={36} />
            <h1 className="text-brand-h2 tracking-tight">BigxBook</h1>
          </div>
          {/* The descriptor, only on the front door. Someone signing in for
              the first time may not yet know what the till covers; once they
              are inside, the navigation says it better than a strapline. */}
          <p className="mb-5 text-label uppercase text-fg-tertiary">
            Billing &middot; POS &middot; Inventory &middot; Accounting
          </p>
          <p className="text-brand-body text-fg-secondary">
            {auth.stage === 'phone' && 'Sign in with your phone number.'}
            {auth.stage === 'otp' && `Enter the code sent to ${auth.phone}.`}
            {auth.stage === 'pin' && `Enter the PIN for ${auth.phone}.`}
            {auth.stage === 'register' && 'Choose a PIN for your new account.'}
            {auth.stage === 'registered' && 'Registration received.'}
          </p>
        </header>

        {auth.stage === 'phone' && (
          <form onSubmit={onSubmitPhone} className="flex flex-col gap-3">
            <label htmlFor="phone" className="text-label uppercase text-fg-tertiary">
              Phone number
            </label>
            <input
              id="phone"
              type="tel"
              inputMode="numeric"
              // Autofill is off across this whole form, deliberately.
              //
              // A till sits on a counter with customers on the other side of
              // it, and it is signed into once and used all day by whoever is
              // standing there. A browser that offers the owner's number and
              // PIN to the next person who taps the field has turned a
              // convenience into a way in.
              //
              // `tel` here beside `current-password` on the PIN is also read
              // by browsers as a username/password PAIR, so both get filled
              // together from whatever was saved — silently replacing a
              // number that was typed correctly.
              autoComplete="off"
              autoFocus
              maxLength={10}
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
              className={FIELD}
              placeholder="9876543210"
            />
            <button type="submit" disabled={auth.busy || !phoneLooksRight} className={PRIMARY}>
              Sign in
            </button>
            {/* Deliberately NOT disabled while the number is short. A disabled
                button is easy to read as decoration, and this one exists to be
                noticed; pressing it early says what is missing instead. */}
            <button type="button" onClick={onClickRegister} className={SECONDARY}>
              Create a new account
            </button>
            {/* Kept for when a DLT-registered SMS template is live. Quiet
                rather than absent: a merchant who has forgotten their PIN can
                still get in this way once SMS is configured. */}
            <button
              type="button"
              onClick={() => phoneLooksRight && void auth.sendOtp(trimmedPhone)}
              disabled={auth.busy || !phoneLooksRight}
              className={QUIET}
            >
              {auth.busy ? 'Sending…' : 'Sign in with OTP instead'}
            </button>
          </form>
        )}

        {auth.stage === 'otp' && (
          <form onSubmit={onSubmitOtp} className="flex flex-col gap-3">
            <label htmlFor="otp" className="text-label uppercase text-fg-tertiary">
              One-time code
            </label>
            <input
              id="otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              className={`${FIELD} text-center text-heading tracking-[0.4em]`}
              placeholder="••••••"
            />
            <button type="submit" disabled={auth.busy || !otp.trim()} className={PRIMARY}>
              {auth.busy ? 'Verifying…' : 'Verify'}
            </button>
            <button type="button" onClick={backToStart} className={QUIET}>
              Use a different number
            </button>
          </form>
        )}

        {auth.stage === 'pin' && (
          <form onSubmit={onSubmitPin} className="flex flex-col gap-3">
            <label htmlFor="pin" className="text-label uppercase text-fg-tertiary">
              PIN
            </label>
            <input
              id="pin"
              // `password`, not a visible field: a till sits on a counter with
              // customers on the other side of it.
              type="password"
              inputMode="numeric"
              // See the phone field above. `off` rather than `one-time-code`:
              // that value makes a phone offer an SMS code from the messages
              // app, which is a different thing entirely and would be worse.
              //
              // Browsers do not always honour `off` on a password field, so
              // this reduces the behaviour rather than guaranteeing it away.
              // The real protection for a shared till is signing out, which
              // is why Sign out sits in the header on every screen.
              autoComplete="off"
              autoFocus
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              className={`${FIELD} text-center text-heading tracking-[0.4em]`}
              placeholder="••••"
              disabled={auth.locked}
            />
            {/* Once locked, retrying is pointless until support resets the PIN,
                so the button goes rather than inviting four more attempts that
                cannot succeed. */}
            {!auth.locked && (
              <button type="submit" disabled={auth.busy || !pin.trim()} className={PRIMARY}>
                {auth.busy ? 'Checking…' : 'Sign in'}
              </button>
            )}
            {/* The way out of the dead end. "Incorrect mobile number or PIN"
                is also what an account that does not exist yet looks like —
                the server will not say which, on purpose — so this is offered
                here rather than only back on the first screen, and it stops
                being a whisper the moment that message appears. */}
            {!auth.locked && (
              <button
                type="button"
                onClick={switchToRegister}
                className={shownError ? SECONDARY : QUIET}
              >
                New here? Create an account instead
              </button>
            )}
            <button type="button" onClick={backToStart} className={QUIET}>
              Use a different number
            </button>
          </form>
        )}

        {auth.stage === 'register' && (
          <form onSubmit={onSubmitRegister} className="flex flex-col gap-3">
            <p className="text-brand-small text-fg-tertiary">
              Registering {auth.phone || trimmedPhone}
            </p>
            <label htmlFor="new-pin" className="text-label uppercase text-fg-tertiary">
              Choose a PIN — 4 or 6 digits
            </label>
            <input
              id="new-pin"
              type="password"
              inputMode="numeric"
              // `off`, not `new-password`: the latter invites the browser to
              // OFFER TO SAVE the PIN, which is the thing being avoided.
              autoComplete="off"
              autoFocus
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              className={`${FIELD} text-center text-heading tracking-[0.4em]`}
              placeholder="••••"
            />
            <label htmlFor="confirm-pin" className="text-label uppercase text-fg-tertiary">
              Confirm PIN
            </label>
            <input
              id="confirm-pin"
              type="password"
              inputMode="numeric"
              // `off`, not `new-password`: the latter invites the browser to
              // OFFER TO SAVE the PIN, which is the thing being avoided.
              autoComplete="off"
              maxLength={6}
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
              className={`${FIELD} text-center text-heading tracking-[0.4em]`}
              placeholder="••••"
            />
            <button type="submit" disabled={auth.busy || !pin || !confirmPin} className={PRIMARY}>
              {auth.busy ? 'Creating your account…' : 'Create account'}
            </button>
            {/* The mirror of the escape hatch on the PIN stage: this number
                already has an account, so the useful next press is the other
                path — not re-reading the sentence that said so. */}
            {auth.alreadyRegistered && (
              <button type="button" onClick={switchToPin} className={SECONDARY}>
                Sign in with your PIN instead
              </button>
            )}
            <button type="button" onClick={backToStart} className={QUIET}>
              Back
            </button>
          </form>
        )}

        {auth.stage === 'registered' && (
          <div className="flex flex-col gap-4">
            <p className="text-brand-body text-fg-secondary">
              {auth.notice ?? 'Registered. Your account is awaiting approval.'}
            </p>
            <p className="text-brand-small text-fg-tertiary">
              We will call {auth.phone} to confirm this number before approving it.
              You can sign in with your PIN once that is done.
            </p>
            <button type="button" onClick={backToStart} className={QUIET}>
              Back to sign in
            </button>
          </div>
        )}

        {auth.stage === 'phone' && (
          <div className="mt-7 border-t border-white/10 pt-5">
            <h2 className="text-brand-body font-semibold text-fg-primary">New to BigxBook?</h2>
            {/* What "Create a new account" above leads to. There is no second
                register button down here any more: one CTA, in the form with
                the number it needs, and this is the explanation behind it. */}
            <ol className="mt-3 flex flex-col gap-1.5 text-brand-small text-fg-tertiary">
              <li>1. Enter your number and choose a PIN</li>
              <li>2. We call you to confirm the number</li>
              <li>3. We review and approve your account</li>
              <li>4. Set up your store and start billing</li>
            </ol>
          </div>
        )}
      </div>

      {/* The parent-brand line sits on the page, below the card, rather than
          inside it: it is provenance, not part of signing in. */}
      <p className="mt-5 text-label uppercase text-fg-tertiary">A Bigxstar Product</p>
    </main>
  );
}
